import { useEffect, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  loginFromUrlSecret,
  resetLocalStateForProfileSwap,
  whoAmI,
} from '../../services/authService';
import { useAuthStore } from '../../store/useAuthStore';
import { useProfileStore } from '../../store/useProfileStore';
import { openExpiredLinkModal } from '../../services/expiredLinkModalService';
import { logDebug, logError } from '../../services/loggerService';

/**
 * Renderless handler that intercepts URLs carrying `?s=<secret>` and runs
 * the auto-login flow before any page-level code reacts. Mounted high in
 * App.tsx (above the gates and routes) — relies on App.tsx already
 * exempting `hasSecretLoginParam` URLs from those gates so we have time
 * to process.
 *
 * Flow:
 *   1. Wait for InitAuth to finish restoring any prior session (so the
 *      profileId comparison below uses a settled value).
 *   2. POST /login-secret with the bare secret. BE validates + sets fresh
 *      session cookies + returns the profileId/userId.
 *   3. Profile-swap detection (per the user's spec):
 *        - If response.profileId === current activeProfile.id  → no reset,
 *          the BE just rotated cookies for the same user.
 *        - Else (different user OR no current user) → resetLocalStateFor-
 *          ProfileSwap() clears local state without calling /auth/logout,
 *          which would clobber the just-set cookies. whoAmI() then
 *          repopulates from the new identity.
 *   4. Strip `?s=` from the URL (history.replace) so refresh doesn't
 *      re-trigger and so it doesn't leak into shared/bookmarked links.
 *   5. On 401 → openExpiredLinkModal(secret) → ExpiredLinkModal handles
 *      the "Resend Link" UX.
 *
 * Guarded by a ref so React's StrictMode double-mount doesn't double-fire
 * the login. We also key the guard by secret-value so a fresh secret
 * (from a resend) processes correctly.
 */
export const SecretLoginHandler: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const authLoading = useAuthStore((s) => s.isLoading);
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const inFlightSecret = useRef<string | null>(null);

  useEffect(() => {
    if (authLoading) return;

    const params = new URLSearchParams(location.search);
    const secret = params.get('s');
    if (!secret) return;

    if (inFlightSecret.current === secret) return;
    inFlightSecret.current = secret;

    const cleanUrl = () => {
      params.delete('s');
      const qs = params.toString();
      const target = location.pathname + (qs ? `?${qs}` : '');
      history.replace(target);
    };

    (async () => {
      try {
        const response = await loginFromUrlSecret(secret);
        logDebug('Secret login succeeded', undefined, {
          incomingProfileId: response.profileId,
          previousProfileId: activeProfile?.id ?? null,
        });

        const sameProfile = activeProfile?.id === response.profileId;
        if (!sameProfile) {
          resetLocalStateForProfileSwap();
        }
        await whoAmI(response.profileId);
        useAuthStore.getState().setAuthenticated(true);
        cleanUrl();
      } catch (err) {
        const status = (err as { status?: number }).status;
        logError(
          err instanceof Error ? err : new Error(String(err)),
          'Secret login failed',
          undefined,
          { status },
        );
        if (status === 401) {
          // Expired or invalid secret — let the modal offer a resend. Keep
          // `?s=` in the URL until close/resend so refreshing during the
          // modal session keeps the same secret context.
          openExpiredLinkModal(secret);
          // Allow retry on the same secret later if the user dismisses
          // (otherwise the ref would block a second pass on next mount).
          inFlightSecret.current = null;
        } else {
          // Unexpected error — strip `?s=` so we don't re-fire on every
          // remount with a hopeless secret. Global error modal from
          // apiService already surfaced a message.
          cleanUrl();
        }
      }
    })();
  }, [location.search, location.pathname, authLoading, activeProfile, history]);

  return null;
};
